import React, { useState } from 'react';
import {
  Tabs,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  message,
  Tag,
  Space,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageContainer from '@/components/PageContainer';
import { useIsMobile } from '@/hooks/useIsMobile';
import * as adminApi from '@/api/admin';
import { useAuthStore } from '@/stores/authStore';
import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;

// ── Role tag colors ──
const ROLE_COLORS: Record<string, string> = {
  admin: 'red',
  user: 'blue',
  ops: 'orange',
  demo: 'green',
};

// ── Action tag colors for audit log ──
const ACTION_COLORS: Record<string, string> = {
  login: 'green',
  login_failed: 'red',
  logout: 'default',
  user_create: 'blue',
  user_delete: 'volcano',
  user_update: 'orange',
  station_owner_change: 'purple',
  station_delete: 'volcano',
  station_unbind: 'magenta',
  password_change: 'cyan',
};

const AUDIT_ACTIONS = [
  'login',
  'login_failed',
  'logout',
  'user_create',
  'user_delete',
  'user_update',
  'station_owner_change',
  'station_delete',
  'station_unbind',
  'password_change',
];

// ── Tab 1: User Management ──
const UserManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<adminApi.UserRow | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [businessForm] = Form.useForm();
  const [businessStations, setBusinessStations] = useState<adminApi.CreateStationItem[]>([{ station_label: '' }]);
  const [businessOpen, setBusinessOpen] = useState(false);

  const { data: users = [], isLoading, refetch } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: adminApi.fetchUsers,
  });

  const createMut = useMutation({
    mutationFn: (values: {
      username: string;
      password: string;
      role: string;
      owner: string | null;
      expires_at: string | null;
    }) =>
      adminApi.createUser(values),
    onSuccess: () => {
      message.success('用户创建成功');
      setCreateOpen(false);
      createForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (error: unknown) => {
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message || '创建失败';
      message.error(msg);
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ username, data }: {
      username: string;
      data: {
        role?: string;
        owner?: string | null;
        is_active?: boolean;
        expires_at?: string | null;
      };
    }) =>
      adminApi.updateUser(username, data),
    onSuccess: () => {
      message.success('用户更新成功');
      setEditOpen(false);
      setEditingUser(null);
      editForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (error: unknown) => {
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message || '更新失败';
      message.error(msg);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (username: string) => adminApi.deleteUser(username),
    onSuccess: () => {
      message.success('用户已删除');
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (error: unknown) => {
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message || '删除失败';
      message.error(msg);
    },
  });

  const businessMut = useMutation({
    mutationFn: (values: { owner_id: string; password: string }) =>
      adminApi.createBusinessAccount({
        owner_id: values.owner_id,
        password: values.password,
        stations: businessStations.filter((s) => s.station_label.trim() !== ''),
      }),
    onSuccess: (result) => {
      message.success(
        `业务账号开通成功: ${result.view_username} / ${result.ops_username}${result.stations_created.length > 0 ? `，${result.stations_created.length} 个场站已创建` : ''}`
      );
      setBusinessOpen(false);
      businessForm.resetFields();
      setBusinessStations([{ station_label: '' }]);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'station-owners'] });
    },
    onError: (error: unknown) => {
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message || '开通失败';
      message.error(msg);
    },
  });

  const columns: ColumnsType<adminApi.UserRow> = [
    { title: '用户名', dataIndex: 'username', key: 'username' },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => <Tag color={ROLE_COLORS[role] || 'default'}>{role}</Tag>,
    },
    {
      title: '归属方',
      dataIndex: 'owner',
      key: 'owner',
      render: (owner: string | null) => owner || '-',
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'default'}>{active ? '启用' : '禁用'}</Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v: string | null) => v || '-',
    },
    {
      title: '到期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (v: string | null) => (
        v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-'
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: unknown, record: adminApi.UserRow) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingUser(record);
              editForm.setFieldsValue({
                role: record.role,
                owner: record.owner || '',
                is_active: record.is_active,
                expires_at: adminApi.toExpiryInputValue(record.expires_at),
              });
              setEditOpen(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除"
            description={`确定要删除用户 "${record.username}" 吗？此操作不可撤销。`}
            onConfirm={() => deleteMut.mutate(record.username)}
            okText="确认"
            cancelText="取消"
            disabled={record.username === 'admin'}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={record.username === 'admin'}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          新增用户
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setBusinessOpen(true)} style={{ background: '#52c41a', borderColor: '#52c41a' }}>
          开通业务账号
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
          刷新
        </Button>
      </Space>
      <Table
        dataSource={users}
        columns={columns}
        rowKey="username"
        loading={isLoading}
        pagination={false}
        size="middle"
        scroll={isMobile ? { x: 900 } : undefined}
      />

      {/* Create User Modal */}
      <Modal
        title="新增用户"
        open={createOpen}
        onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
        onOk={() => createForm.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={(values) => {
            createMut.mutate({
              ...values,
              owner: values.owner || null,
              expires_at: adminApi.toExpiryIso(values.expires_at),
            });
          }}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder="用户名" />
          </Form.Item>
          <Form.Item
            name="password"
            label="初始密码"
            rules={[
              { required: true, message: '请输入初始密码' },
              { min: 4, message: '密码至少4个字符' },
            ]}
          >
            <Input.Password placeholder="初始密码" />
          </Form.Item>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select
              placeholder="选择角色"
              options={[
                { label: 'admin', value: 'admin' },
                { label: 'user', value: 'user' },
                { label: 'ops', value: 'ops' },
                { label: 'demo', value: 'demo' },
              ]}
            />
          </Form.Item>
          <Form.Item name="owner" label="归属方">
            <Input placeholder="归属方ID，如 daokou（admin留空）" />
          </Form.Item>
          <Form.Item name="expires_at" label="到期时间">
            <Input type="datetime-local" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Business Account Modal */}
      <Modal
        title="开通业务账号"
        open={businessOpen}
        onCancel={() => { setBusinessOpen(false); businessForm.resetFields(); setBusinessStations([{ station_label: '' }]); }}
        onOk={() => businessForm.submit()}
        confirmLoading={businessMut.isPending}
        width={isMobile ? '95%' : 720}
        destroyOnClose
      >
        <Form
          form={businessForm}
          layout="vertical"
          onFinish={(values) => businessMut.mutate(values)}
        >
          <Form.Item
            name="owner_id"
            label="业务方标识"
            rules={[
              { required: true, message: '请输入业务方标识' },
              { pattern: /^[a-zA-Z0-9_-]+$/, message: '仅支持英文、数字、下划线和连字符' },
            ]}
          >
            <Input placeholder="如 huanghe，自动创建 huanghe / huanghe-ops 双账号" />
          </Form.Item>
          <Form.Item
            name="password"
            label="初始密码"
            rules={[
              { required: true, message: '请输入初始密码' },
              { min: 4, message: '密码至少4个字符' },
            ]}
          >
            <Input.Password placeholder="两个账号共用此密码" />
          </Form.Item>
          <Form.Item label="场站列表（可选，后续可在场站归属中补充）">
            {businessStations.map((st, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <Input
                  placeholder="场站名"
                  value={st.station_label}
                  onChange={(e) => {
                    const next = [...businessStations];
                    next[i] = { ...next[i], station_label: e.target.value };
                    setBusinessStations(next);
                  }}
                  style={{ flex: 2 }}
                />
                <Input
                  placeholder="光伏(MW)"
                  type="number"
                  value={st.pv_capacity ?? ''}
                  onChange={(e) => {
                    const next = [...businessStations];
                    next[i] = { ...next[i], pv_capacity: e.target.value ? Number(e.target.value) : undefined };
                    setBusinessStations(next);
                  }}
                  style={{ flex: 1 }}
                />
                <Input
                  placeholder="储能功率(MW)"
                  type="number"
                  value={st.bat_p ?? ''}
                  onChange={(e) => {
                    const next = [...businessStations];
                    next[i] = { ...next[i], bat_p: e.target.value ? Number(e.target.value) : undefined };
                    setBusinessStations(next);
                  }}
                  style={{ flex: 1 }}
                />
                <Input
                  placeholder="储能容量(MWh)"
                  type="number"
                  value={st.bat_c ?? ''}
                  onChange={(e) => {
                    const next = [...businessStations];
                    next[i] = { ...next[i], bat_c: e.target.value ? Number(e.target.value) : undefined };
                    setBusinessStations(next);
                  }}
                  style={{ flex: 1 }}
                />
                <Input
                  placeholder="补贴电价"
                  type="number"
                  value={st.subsidy_price ?? ''}
                  onChange={(e) => {
                    const next = [...businessStations];
                    next[i] = { ...next[i], subsidy_price: e.target.value ? Number(e.target.value) : undefined };
                    setBusinessStations(next);
                  }}
                  style={{ flex: 1 }}
                />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (businessStations.length === 1) {
                      setBusinessStations([{ station_label: '' }]);
                    } else {
                      setBusinessStations(businessStations.filter((_, idx) => idx !== i));
                    }
                  }}
                />
              </div>
            ))}
            <Button
              type="dashed"
              onClick={() => setBusinessStations([...businessStations, { station_label: '' }])}
              block
              icon={<PlusOutlined />}
            >
              添加场站
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title={`编辑用户: ${editingUser?.username || ''}`}
        open={editOpen}
        onCancel={() => { setEditOpen(false); setEditingUser(null); editForm.resetFields(); }}
        onOk={() => editForm.submit()}
        confirmLoading={updateMut.isPending}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => {
            if (editingUser) {
              updateMut.mutate({
                username: editingUser.username,
                data: {
                  role: values.role,
                  owner: values.owner || null,
                  is_active: values.is_active,
                  expires_at: adminApi.toExpiryIso(values.expires_at),
                },
              });
            }
          }}
        >
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select
              placeholder="选择角色"
              options={[
                { label: 'admin', value: 'admin' },
                { label: 'user', value: 'user' },
                { label: 'ops', value: 'ops' },
                { label: 'demo', value: 'demo' },
              ]}
            />
          </Form.Item>
          <Form.Item name="owner" label="归属方">
            <Input placeholder="归属方ID（admin留空）" />
          </Form.Item>
          <Form.Item name="is_active" label="启用状态" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="expires_at" label="到期时间">
            <Input type="datetime-local" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ── Tab 2: Station Owner Management ──
const StationOwnerManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [editingStation, setEditingStation] = useState<adminApi.StationOwnerRow | null>(null);
  const [editForm] = Form.useForm();

  const { data: stations = [], isLoading, refetch } = useQuery({
    queryKey: ['admin', 'station-owners'],
    queryFn: adminApi.fetchStationOwners,
  });

  const updateMut = useMutation({
    mutationFn: ({ station, owner }: { station: string; owner: string }) =>
      adminApi.updateStationOwner(station, owner),
    onSuccess: () => {
      message.success('场站归属更新成功');
      setEditOpen(false);
      setEditingStation(null);
      editForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ['admin', 'station-owners'] });
    },
    onError: (error: unknown) => {
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message || '更新失败';
      message.error(msg);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (station: string) => adminApi.deleteStation(station),
    onSuccess: () => {
      message.success('场站已删除');
      queryClient.invalidateQueries({ queryKey: ['admin', 'station-owners'] });
    },
    onError: (error: unknown) => {
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message || '删除失败';
      message.error(msg);
    },
  });

  const columns: ColumnsType<adminApi.StationOwnerRow> = [
    { title: '场站', dataIndex: 'station_label', key: 'station_label' },
    {
      title: '当前归属',
      dataIndex: 'owner',
      key: 'owner',
      render: (owner: string | null) => (
        <Tag color={owner ? 'blue' : 'default'}>{owner || '未分配'}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: unknown, record: adminApi.StationOwnerRow) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingStation(record);
              editForm.setFieldsValue({ owner: record.owner || '' });
              setEditOpen(true);
            }}
          >
            修改归属
          </Button>
          <Popconfirm
            title="确认删除"
            description={`确定要删除场站 "${record.station_label}" 吗？此操作不可撤销。`}
            onConfirm={() => deleteMut.mutate(record.station_label)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ReloadOutlined />} onClick={() => refetch()}>刷新</Button>
      </Space>
      <Table
        dataSource={stations}
        columns={columns}
        rowKey="station_label"
        loading={isLoading}
        pagination={false}
        size="middle"
        scroll={isMobile ? { x: 500 } : undefined}
      />

      {/* Edit Station Owner Modal */}
      <Modal
        title={`修改场站归属: ${editingStation?.station_label || ''}`}
        open={editOpen}
        onCancel={() => { setEditOpen(false); setEditingStation(null); editForm.resetFields(); }}
        onOk={() => editForm.submit()}
        confirmLoading={updateMut.isPending}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => {
            if (editingStation) {
              updateMut.mutate({
                station: editingStation.station_label,
                owner: values.owner,
              });
            }
          }}
        >
          <Form.Item
            name="owner"
            label="归属方"
            rules={[{ required: true, message: '请输入归属方ID' }]}
          >
            <Input placeholder="归属方ID，如 daokou" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ── Tab 3: Audit Log ──
const AuditLogViewer: React.FC = () => {
  const isMobile = useIsMobile();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [actionFilter, setActionFilter] = useState<string | undefined>();
  const [usernameFilter, setUsernameFilter] = useState('');

  const filters = {
    page,
    page_size: pageSize,
    ...(actionFilter ? { action: actionFilter } : {}),
    ...(usernameFilter ? { username: usernameFilter } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'audit-log', filters],
    queryFn: () => adminApi.fetchAuditLog(filters),
    placeholderData: (prev) => prev,
  });

  const logs = data?.data ?? [];
  const total = data?.total ?? 0;

  const columns: ColumnsType<adminApi.AuditLogRow> = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
    },
    { title: '操作人', dataIndex: 'username', key: 'username', width: 120 },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 140,
      render: (action: string) => (
        <Tag color={ACTION_COLORS[action] || 'default'}>{action}</Tag>
      ),
    },
    {
      title: '目标',
      dataIndex: 'target',
      key: 'target',
      width: 120,
      render: (v: string | null) => v || '-',
    },
    {
      title: '详情',
      dataIndex: 'detail',
      key: 'detail',
      ellipsis: true,
      render: (v: string | null) => {
        if (!v) return '-';
        try {
          const parsed = JSON.parse(v);
          return (
            <Text
              ellipsis={{ tooltip: JSON.stringify(parsed, null, 2) }}
              style={{ maxWidth: 300 }}
            >
              {JSON.stringify(parsed)}
            </Text>
          );
        } catch {
          return (
            <Text ellipsis={{ tooltip: v }} style={{ maxWidth: 300 }}>
              {v}
            </Text>
          );
        }
      },
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="操作类型"
          allowClear
          style={{ width: 180 }}
          value={actionFilter}
          onChange={(v) => { setActionFilter(v); setPage(1); }}
          options={AUDIT_ACTIONS.map((a) => ({ label: a, value: a }))}
        />
        <Input.Search
          placeholder="搜索操作人"
          allowClear
          style={{ width: 200 }}
          value={usernameFilter}
          onChange={(e) => setUsernameFilter(e.target.value)}
          onSearch={(v) => { setUsernameFilter(v); setPage(1); }}
        />
      </Space>
      <Table
        dataSource={logs}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        size="middle"
        scroll={isMobile ? { x: 700 } : undefined}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t) => `共 ${t} 条记录`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
};

// ── Admin Page ──
const AdminPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);

  if (user?.role !== 'admin') {
    return (
      <PageContainer title="用户管理">
        <Card>
          <p>仅管理员可访问此页面</p>
        </Card>
      </PageContainer>
    );
  }

  const tabItems = [
    { key: 'users', label: '用户管理', children: <UserManagement /> },
    { key: 'stations', label: '场站归属管理', children: <StationOwnerManagement /> },
    { key: 'audit', label: '操作日志', children: <AuditLogViewer /> },
  ];

  return (
    <PageContainer title="用户管理">
      <Card>
        <Tabs defaultActiveKey="users" items={tabItems} />
      </Card>
    </PageContainer>
  );
};

export default AdminPage;
